import { InputHTMLAttributes, ReactNode } from "react";
import cn from "classnames";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string | ReactNode;
  variant?: "sm" | "md";
  labelSize?: "xs" | "sm" | "base";
  id?: string;
  // "row" (default): Preferences-style label left, switch right, 40px tall.
  // "compact": a small bold title with the switch centered beside it and an
  // optional description spanning the full width underneath. Built for narrow
  // sidebars, where sharing a line with the switch squeezed the description
  // to one word per line at 150% browser zoom.
  layout?: "row" | "compact";
  description?: ReactNode;
}

export default function Toggle({
  className,
  label,
  variant = "md",
  labelSize = "base",
  id,
  layout = "row",
  description,
  ...props
}: InputProps) {
  // Generate a unique ID that works with both string and ReactNode labels
  const uniqueId =
    id ||
    `toggle-${
      typeof label === "string"
        ? label.toLowerCase().replace(/\s+/g, "-")
        : "custom"
    }`;

  // For aria-label, ensure we have a string
  const ariaLabel = typeof label === "string" ? label : "Toggle option";

  const inputClassName = cn(
    "toggle toggle-md border-2 border-gray-400 bg-gray-400",
    "checked:border-blue-500 checked:bg-blue-500",
    "[&:not(:checked)]:bg-gray-400",
    "[&:after]:!bg-white",
    className
  );

  if (layout === "compact") {
    return (
      <div>
        <div className="flex items-center justify-between gap-3">
          <label
            htmlFor={uniqueId}
            className="m-0 min-w-0 text-xs font-semibold text-base-content cursor-pointer"
          >
            {label}
          </label>
          <input
            id={uniqueId}
            className={cn(inputClassName, "shrink-0")}
            type="checkbox"
            aria-label={ariaLabel}
            {...props}
          />
        </div>
        {description && (
          <p className="m-0 mt-0.5 text-[11px] text-base-content/60 leading-snug">
            {description}
          </p>
        )}
      </div>
    );
  }

  return (
    <fieldset className="form-control">
      <div className={cn("label p-0", labelSize === "xs" ? "min-h-7 mb-1" : "min-h-10")}>
        <span
          className={cn(
            "flex items-center text-gray-800 dark:text-base-content/80 font-medium",
            labelSize === "xs" ? "text-sm" : "text-xs",
            labelSize === "xs" ? "md:text-sm" : labelSize === "sm" ? "md:text-sm" : "md:text-base"
          )}
        >
          {label}
        </span>
        <input
          id={uniqueId}
          className={inputClassName}
          type="checkbox"
          aria-label={ariaLabel}
          {...props}
        />
      </div>
    </fieldset>
  );
}
